/**
 * NC (National Contract) template logic: FIELD_DEPENDENCIES for NC and recalculateFieldsNC.
 * Delegates to recalcCore with NC config (formulas + getBlendedWhlsFee).
 */

import type { RecalculateFieldsParams } from '../../shared/logic';
import { recalculateFieldsCore } from '../../recalcCore';
import { getNCRecalcConfig } from '../../recalcConfigBuilders';

/** NC-specific field dependencies (subset used by NC template). */
export const FIELD_DEPENDENCIES_NC: Record<string, string[]> = {
  'gsmsCostFromMfg': ['gsmsNetCostFromMfg', 'gsmsCost', 'gsmsProfit', 'suggestedBidPrice'],
  'gsmsNetCostFromMfg': ['gsmsCost', 'gsmsProfit', 'gsmsMarketingFee'],
  'mfgBottleSize': ['gsmsCost'],
  'gsmsPackSize': ['gsmsBottleCapacity', 'gsmsBottleCost', 'gsmsCapCost', 'gsmsLabelCost', 'gsmsInsertCost', 'gsmsBoxCartonCost', 'gsmsFrtCost', 'pv2Percent', 'gsmsLabor', 'gsmsCost'],
  'gsmsHndlgType': ['gsmsBottleCapacity', 'gsmsBottleCost', 'outsourceRepackFee', 'gsmsLabor', 'gsmsCost', 'gsmsMarketingFee'],
  'gsmsBottleCapacity': ['gsmsBottleCost', 'gsmsCapCost', 'gsmsLabelCost', 'gsmsInsertCost', 'gsmsBoxCartonCost', 'gsmsFrtCost', 'pv2Percent', 'gsmsLabor', 'gsmsMarketingFee'],
  'gsmsIffFee': ['gsmsCost', 'gsmsProfit'],
  'wac': ['pv2Percent'],
  'pv2Percent': ['gsmsCost'],
  'suggestedBidPrice': ['gsmsIffFee', 'gsmsCost', 'gsmsProfit', 'pv2Percent', 'wac', 'gsmsMarketingFee', 'annualTmopSavings', 'monthlyTmopSavings', 'govSavingsAnalysis', 'blendedWhlsFee', 'contractAnnualRevenue', 'contractAnnualGP', 'contractAnnualCost'],
  'gsmsProfit': ['gsmsNetCostFromMfg', 'gsmsCost'],
  'gsmsCost': ['gsmsProfit'],
  'vaAnnualNcEst': ['ncEstAnnualUsage', 'contractAnnualRevenue', 'contractAnnualGP', 'contractAnnualCost', 'blendedWhlsFee'],
  'dodAnnualNcEst': ['ncEstAnnualUsage', 'contractAnnualRevenue', 'contractAnnualGP', 'contractAnnualCost', 'blendedWhlsFee'],
  'ihsAnnualNcEst': ['ncEstAnnualUsage', 'contractAnnualRevenue', 'contractAnnualGP', 'contractAnnualCost', 'blendedWhlsFee'],
  'bopAnnualNcEst': ['ncEstAnnualUsage', 'contractAnnualRevenue', 'contractAnnualGP', 'contractAnnualCost', 'blendedWhlsFee'],
  'ncEstAnnualUsage': ['contractAnnualRevenue', 'contractAnnualGP', 'contractAnnualCost'],
  'usagePeriodStartDate': ['blendedWhlsFee'],
  'usagePeriodEndDate': ['blendedWhlsFee'],
  'tmopPercent': ['annualTmopSavings', 'monthlyTmopSavings'],
  'lowestContractPrice': ['annualTmopSavings', 'monthlyTmopSavings', 'govSavingsAnalysis'],
  'targetLaunchDate': ['blendedWhlsFee'],
};

/**
 * Recalculate fields for NC template. Uses recalcCore with NC config.
 */
export function recalculateFieldsNC(params: RecalculateFieldsParams): Record<string, any> {
  return recalculateFieldsCore({ ...params, templateType: 'nc' }, getNCRecalcConfig(FIELD_DEPENDENCIES_NC));
}
